import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowDownLeft, ArrowUpRight, ChevronDown, Sparkles } from 'lucide-react';
import { formatTime } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import type { HistoryEntry, HistoryItem } from '../model/history';

/** Badge que corta com reticências; o clique abre o texto completo (funciona no toque). */
function DetailBadge({
  text,
  variant = 'outline',
  className,
}: {
  text: string;
  variant?: BadgeProps['variant'];
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex max-w-full min-w-0 touch-manipulation rounded-full"
          aria-label={text}
        >
          <Badge
            variant={variant}
            className={cn('max-w-full min-w-0 overflow-hidden font-normal', className)}
          >
            <span className="min-w-0 truncate">{text}</span>
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="max-w-[min(20rem,calc(100vw-2rem))] px-3 py-2 text-sm leading-snug"
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

function ItemsTable({ items }: { items: HistoryItem[] }) {
  return (
    <table className="w-full table-fixed">
      <caption className="sr-only">Itens da compra</caption>
      <colgroup>
        <col className="w-[20%]" />
        <col />
        <col className="w-[16%]" />
        <col className="w-[24%]" />
      </colgroup>
      <thead>
        <tr className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <th scope="col" className="px-1 pb-1.5 text-center font-semibold">
            SKU
          </th>
          <th scope="col" className="px-1 pb-1.5 text-center font-semibold">
            Produto
          </th>
          <th scope="col" className="px-1 pb-1.5 text-center font-semibold">
            Qtde
          </th>
          <th scope="col" className="px-1 pb-1.5 text-center font-semibold">
            Valor
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => (
          <tr key={`${item.sku ?? item.name}-${index}`}>
            <td className="min-w-0 px-1 py-1 text-center align-middle">
              {item.sku ? <DetailBadge text={item.sku} className="font-mono" /> : null}
            </td>
            <td className="min-w-0 px-1 py-1 text-center align-middle">
              <DetailBadge text={item.name} variant="secondary" />
            </td>
            <td className="min-w-0 px-1 py-1 text-center align-middle">
              {item.quantity ? (
                <DetailBadge text={`${item.quantity} un`} className="tabular-nums" />
              ) : null}
            </td>
            <td className="min-w-0 px-1 py-1 text-center align-middle">
              {item.price ? (
                <DetailBadge text={item.price} variant="default" className="tabular-nums" />
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Uma movimentação do cartão (Histórico e "Últimas movimentações" do Início). */
export function HistoryEntryRow({ entry }: { entry: HistoryEntry }) {
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const Icon = entry.direction === 'in' ? ArrowDownLeft : entry.direction === 'out' ? ArrowUpRight : Sparkles;
  const date = new Date(entry.date);
  const when = Number.isNaN(date.getTime())
    ? ''
    : `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} às ${formatTime(date)}`;
  const expandable = Boolean(entry.validUntil || entry.price || entry.items.length);
  const label = [entry.title, entry.delta, when].filter(Boolean).join(', ');

  const row = (
    <div className="flex min-h-16 items-center gap-3 py-3">
      <span
        className={cn(
          'grid size-10 shrink-0 place-items-center rounded-full',
          entry.direction === 'in' && 'bg-success/15 text-success',
          entry.direction === 'out' && 'bg-foreground/[0.06] text-foreground',
          entry.direction === 'neutral' && 'bg-primary/15 text-primary',
        )}
        aria-hidden
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold leading-tight">{entry.title}</p>
        {when ? <p className="mt-0.5 text-xs text-muted-foreground">{when}</p> : null}
      </div>
      {entry.delta ? (
        <span
          className={cn(
            'shrink-0 text-[15px] font-bold tabular-nums',
            entry.direction === 'in' ? 'text-success' : 'text-foreground',
          )}
        >
          {entry.delta}
        </span>
      ) : null}
      {expandable ? (
        <ChevronDown
          className={cn('size-4 shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')}
          aria-hidden
        />
      ) : null}
    </div>
  );

  return (
    <li>
      {expandable ? (
        <button
          type="button"
          className="w-full text-left"
          aria-expanded={open}
          aria-label={`${label}. ${open ? 'Ocultar detalhes' : 'Ver detalhes'}`}
          onClick={() => setOpen((v) => !v)}
        >
          {row}
        </button>
      ) : (
        <div aria-label={label}>{row}</div>
      )}
      <AnimatePresence initial={false}>
        {open && expandable ? (
          <motion.div
            key="detail"
            initial={reduceMotion ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduceMotion ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-3 pb-4">
              {entry.validUntil || entry.price ? (
                <div className="flex items-center justify-between gap-2 px-1">
                  {entry.validUntil ? <DetailBadge text={entry.validUntil} /> : <span />}
                  {entry.price ? (
                    <DetailBadge text={`Compra de ${entry.price}`} variant="secondary" />
                  ) : null}
                </div>
              ) : null}
              {entry.items.length ? (
                <div className="px-1">
                  <ItemsTable items={entry.items} />
                </div>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </li>
  );
}
